import React, { useState } from "react";
import { UploadCloud, FileSpreadsheet, X, Loader2, Keyboard, Plus, Trash2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { parseCsv, autoMap } from "../../lib/csv";
import { useToast } from "../../components/ui/toast";
import { Dropdown } from "../../components/ui/dropdown";
import { createContactBatch } from "./api";
import { cn } from "../../lib/utils";

interface UploadContactBatchModalProps {
  onClose: () => void;
  /** Tags the created batch's origin (e.g. "crm" when opened from CRM →
   * Batches) so each section's list can filter to only its own. */
  source?: string;
  /** Called once the batch (and its CRM leads) are created, in addition to
   * the react-query cache invalidation below — for callers like the Leads
   * page that keep their own list in state rather than via react-query. */
  onUploaded?: () => void;
}

type Mode = "file" | "manual";
type ManualRow = { name: string; phone: string; contactId: string };

const emptyRow = (): ManualRow => ({ name: "", phone: "", contactId: "" });

// Custom CONTACT ID rules — must match backend
// (schedules/schemas.py CUSTOM_CONTACT_ID_RE + contact_batches/service.py).
const CONTACT_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_.\-/]*$/;
const GENERATED_CONTACT_ID_RE = /^cont-[0-9a-f]{8}$/;
const contactIdError = (value: string): string | null => {
  const v = value.trim();
  if (!v) return null;
  if (v.length > 120) return "max 120 characters";
  if (!CONTACT_ID_RE.test(v)) return "letters/digits/_ . - / only";
  if (GENERATED_CONTACT_ID_RE.test(v)) return "looks auto-generated — use your own id";
  return null;
};

export function UploadContactBatchModal({ onClose, source, onUploaded }: UploadContactBatchModalProps) {
  const [mode, setMode] = useState<Mode>("file");
  const [parsing, setParsing] = useState(false);
  const [file, setFile] = useState<{ name: string; size: number } | null>(null);
  const [batchName, setBatchName] = useState("");
  const [tags, setTags] = useState("");

  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Manual-entry rows
  const [manualRows, setManualRows] = useState<ManualRow[]>([emptyRow(), emptyRow(), emptyRow()]);

  const toast = useToast();
  const queryClient = useQueryClient();

  const coreFields = [
    { key: "phone_number", label: "Phone number", required: true },
    { key: "name", label: "Name", required: false },
    { key: "contact_id", label: "Contact ID (optional — your stable id)", required: false },
  ];

  const handleFile = async (f: File) => {
    setParsing(true);
    try {
      const { rows, headers: csvHeaders } = await parseCsv(f);
      if (rows.length === 0) {
        toast.danger("Empty file", "No data found in the CSV.");
        setParsing(false);
        return;
      }
      setFile({ name: f.name, size: f.size });
      setHeaders(csvHeaders);
      setRawRows(rows);
      const defaultName = f.name.replace(/\.[^/.]+$/, "");
      setBatchName(defaultName);
      setMapping(autoMap(coreFields as any, csvHeaders));
    } catch (err) {
      toast.danger("Couldn't parse CSV", "Ensure it has headers and is valid UTF-8.");
    }
    setParsing(false);
  };

  const resetFile = () => {
    setFile(null);
    setHeaders([]);
    setRawRows([]);
    setMapping({});
    setBatchName("");
  };

  const updateManualRow = (i: number, patch: Partial<ManualRow>) => {
    setManualRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const addManualRow = () => setManualRows((rows) => [...rows, emptyRow()]);
  const removeManualRow = (i: number) =>
    setManualRows((rows) => (rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows));

  const manualValidCount = manualRows.filter((r) => r.phone.trim()).length;

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!batchName.trim()) throw new Error("Batch name is required");

      let contacts: { phone_number: string; name?: string; contact_id?: string; metadata?: Record<string, string> }[];

      const assertContactIds = (ids: string[]) => {
        const seen = new Set<string>();
        for (const raw of ids) {
          const v = raw.trim();
          if (!v) continue;
          const err = contactIdError(v);
          if (err) throw new Error(`Contact ID "${v}": ${err}`);
          const lc = v.toLowerCase();
          if (seen.has(lc)) throw new Error(`Duplicate Contact ID "${v}" — each row needs its own id.`);
          seen.add(lc);
        }
      };

      if (mode === "manual") {
        assertContactIds(manualRows.map((r) => r.contactId));
        contacts = manualRows
          .map((r) => ({
            phone_number: r.phone.trim(),
            name: r.name.trim() || undefined,
            contact_id: r.contactId.trim() || undefined,
            metadata: {},
          }))
          .filter((c) => !!c.phone_number);
        if (contacts.length === 0) throw new Error("Add at least one row with a phone number.");
      } else {
        const phoneCol = mapping["phone_number"];
        if (!phoneCol) throw new Error("Phone number column must be mapped");
        const nameCol = mapping["name"];
        const contactCol = mapping["contact_id"];
        contacts = rawRows
          .map((row) => {
            const metadata: Record<string, string> = {};
            for (const [key, value] of Object.entries(row)) {
              if (key !== phoneCol && key !== nameCol && key !== contactCol) metadata[key] = value;
            }
            return {
              phone_number: row[phoneCol],
              name: (nameCol ? row[nameCol] : undefined) || undefined,
              contact_id: (contactCol ? row[contactCol] : undefined)?.trim() || undefined,
              metadata,
            };
          })
          .filter((c) => !!c.phone_number?.trim());
        if (contacts.length === 0) throw new Error("No valid contacts with phone numbers found.");
        assertContactIds(contacts.map((c) => c.contact_id || ""));
      }

      return createContactBatch({ name: batchName.trim(), tags: tags.split(",").map(tag => tag.trim()).filter(Boolean), contacts, source });
    },
    onSuccess: () => {
      toast.success("Contact batch created");
      queryClient.invalidateQueries({ queryKey: ["contact-batches"] });
      // Batch contacts are created as CRM leads too (techladder-core's
      // create_leads_for_batch) — refresh every cache keyed on crm-leads so
      // pages showing leads pick them up without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ["crm-leads"] });
      queryClient.invalidateQueries({ queryKey: ["crm", "leads"] });
      onUploaded?.();
      onClose();
    },
    onError: (err: any) => {
      toast.danger("Couldn't create batch", err.message);
    },
  });

  const canSave =
    !!batchName.trim() &&
    !saveMutation.isPending &&
    (mode === "manual" ? manualValidCount > 0 : !!file && !!mapping["phone_number"]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-ink/40 p-4 backdrop-blur-sm sm:p-6">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-canvas shadow-2xl animate-in zoom-in-95">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 className="text-sm font-semibold text-ink">Add Contacts</h2>
          <button onClick={onClose} className="rounded-lg p-2 text-ink-3 hover:bg-soft hover:text-ink">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-1 border-b border-line bg-soft/30 px-6 pt-4">
          {([
            ["file", "Upload file", UploadCloud],
            ["manual", "Enter manually", Keyboard],
          ] as const).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setMode(key)}
              className={cn(
                "flex items-center gap-1.5 rounded-t-lg border border-b-0 px-4 py-2 text-sm font-semibold transition-colors",
                mode === key
                  ? "border-line bg-canvas text-ink"
                  : "border-transparent text-ink-3 hover:text-ink",
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <label className="block text-sm">Batch tags<input value={tags} maxLength={1000} onChange={e => setTags(e.target.value)} placeholder="September, Referral" className="mt-2 w-full rounded-lg border border-line bg-transparent px-3 py-2" /></label>
          {mode === "manual" ? (
            <>
              <div>
                <label className="mb-1 block text-sm font-semibold text-ink">Batch Name</label>
                <input
                  type="text"
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  placeholder="e.g. VIP callbacks — 27 Aug"
                  className="w-full rounded-lg border border-line bg-canvas px-3 py-2 text-sm focus:border-brand focus:outline-none"
                />
              </div>

              <div className="overflow-hidden rounded-xl border border-line">
                <div className="grid grid-cols-[1fr_1fr_1fr_40px] gap-2 border-b border-line bg-soft px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-ink-3">
                  <span>Contact ID (optional)</span>
                  <span>Name</span>
                  <span>Phone number *</span>
                  <span />
                </div>
                <div className="max-h-[280px] overflow-y-auto divide-y divide-line">
                  {manualRows.map((row, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_40px] items-center gap-2 px-3 py-2">
                      <input
                        type="text"
                        value={row.contactId}
                        onChange={(e) => updateManualRow(i, { contactId: e.target.value })}
                        placeholder="cust-001"
                        title="Your stable id for this contact — same id shows in batch + lead + dial. Leave blank to auto-assign."
                        className="w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 font-mono text-sm focus:border-brand focus:outline-none"
                      />
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => updateManualRow(i, { name: e.target.value })}
                        placeholder="Optional"
                        className="w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none"
                      />
                      <input
                        type="tel"
                        value={row.phone}
                        onChange={(e) => updateManualRow(i, { phone: e.target.value })}
                        placeholder="+91 98xxxxxxxx"
                        className="w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 text-sm font-mono focus:border-brand focus:outline-none"
                      />
                      <button
                        onClick={() => removeManualRow(i)}
                        disabled={manualRows.length === 1}
                        className="flex h-8 w-8 items-center justify-center rounded-md text-ink-3 hover:bg-danger-soft hover:text-danger disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Remove row"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addManualRow}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-line bg-soft/40 py-2 text-xs font-semibold text-ink-2 hover:bg-soft transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" /> Add row
                </button>
              </div>
              <p className="text-xs text-ink-3">
                {manualValidCount} contact{manualValidCount === 1 ? "" : "s"} ready. Rows without a phone
                number are ignored. Contact ID is optional — same id shows in batch + lead + dial; leave
                blank to auto-assign.
              </p>
            </>
          ) : !file ? (
            <label className="flex min-h-[260px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-line bg-soft/30 p-10 text-center transition-colors hover:border-brand hover:bg-brand-soft/10">
              <UploadCloud className="h-12 w-12 text-ink-3" />
              <p className="mt-4 text-base font-semibold text-ink">{parsing ? "Parsing…" : "Drop your CSV or Excel file here"}</p>
              <p className="mt-1 text-sm text-ink-3">or click to browse from your computer</p>
              <input
                type="file"
                accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,application/vnd.ms-excel"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
            </label>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-xl border border-brand/20 bg-brand-soft/20 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-6 w-6 text-brand" />
                    <div>
                      <p className="text-sm font-semibold text-brand">{file.name}</p>
                      <p className="text-xs text-brand/70">{(file.size / 1024).toFixed(1)} KB • {rawRows.length} rows</p>
                    </div>
                  </div>
                  <button onClick={resetFile} className="rounded-lg p-1.5 text-brand/60 hover:bg-brand/10 hover:text-brand" aria-label="Remove file">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-semibold text-ink">Batch Name</label>
                  <input
                    type="text"
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                    placeholder="e.g. August 2026 Leads"
                    className="w-full rounded-lg border border-line bg-canvas px-3 py-2 text-sm focus:border-brand focus:outline-none"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-line bg-soft/30 p-4">
                <div className="mb-4">
                  <p className="text-sm font-bold text-ink">Map Columns</p>
                  <p className="text-xs text-ink-3">Match your spreadsheet columns to the required fields. Map a Contact ID column (e.g. cust-001) to keep the same id in batch, lead and dial — leave unmapped to auto-assign. Unmapped columns will be saved securely as metadata.</p>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {coreFields.map((field) => (
                    <div key={field.key}>
                      <span className="mb-1 block truncate text-[10px] font-semibold uppercase text-ink-3">
                        {field.label} {field.required && <span className="text-danger">*</span>}
                      </span>
                      <Dropdown
                        ariaLabel={`Map ${field.label}`}
                        value={mapping[field.key] || ""}
                        options={[
                          { value: "", label: "Not mapped" },
                          ...headers.map((h) => ({ value: h, label: h })),
                        ]}
                        onChange={(val) => setMapping({ ...mapping, [field.key]: val })}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-line bg-elevated">
                <div className="border-b border-line px-4 py-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-ink-3">
                    Data Preview ({rawRows.length} row{rawRows.length === 1 ? "" : "s"})
                  </span>
                </div>
                <div className="max-h-[360px] overflow-auto">
                  <table className="w-full text-left text-xs text-ink">
                    <thead className="sticky top-0 bg-soft text-ink-2 border-b border-line">
                      <tr>
                        <th className="w-10 px-3 py-2 font-semibold whitespace-nowrap">#</th>
                        {headers.map((k) => <th key={k} className="px-4 py-2 font-semibold whitespace-nowrap">{k}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {rawRows.map((row, i) => (
                        <tr key={i} className="hover:bg-soft/50">
                          <td className="px-3 py-2 text-ink-3">{i + 1}</td>
                          {headers.map((k, j) => (
                            <td key={j} className="max-w-[150px] truncate px-4 py-2">{row[k]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-line bg-soft/30 px-6 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Cancel
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!canSave}
            className="flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-canvas hover:bg-ink-2 disabled:opacity-50"
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save Batch
          </button>
        </div>
      </div>
    </div>
  );
}
